<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import ImockupPresetChips from '@/components/imockup/ImockupPresetChips.vue'
import ImockupRatioGrid from '@/components/imockup/ImockupRatioGrid.vue'
import { useEditorAuth } from '@/composables/useEditorAuth'
import {
  RESOLUTION_PRESETS,
  type RatioPresetId,
  type ResolutionPresetId,
} from '@/constants/mockupDevices'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const { session } = useEditorAuth()

/** 会话中的 `membershipTier` 为 `lifetime` 时视为终身会员（仅前端展示与交互，不做鉴权）。 */
const canUse4KResolution = computed(() => {
  const s = session.value
  if (!s?.token) return false
  return s.membershipTier === 'lifetime'
})

/** 注意：`storeToRefs` 不包含非 ref 的 store 字段，分辨率选项须从常量模块引入。 */
const { sceneWidth, sceneHeight, activeResolutionPreset, activeRatioPreset, exportOutputWidth, exportOutputHeight } =
  storeToRefs(store)

function resolutionOptionDisabled(opt: string): boolean {
  return opt === '4K' && !canUse4KResolution.value
}

function resolutionOptionTitle(opt: string): string | undefined {
  if (opt === '4K' && !canUse4KResolution.value) {
    return store.tUi('inspectorScene.resolution4kLifetimeOnly')
  }
  return undefined
}

function onResolution(value: string): void {
  if (value === '4K' && !canUse4KResolution.value) return
  store.applyResolutionPreset(value as ResolutionPresetId)
}

function onRatio(value: RatioPresetId): void {
  store.applyRatioPreset(value)
}
</script>

<template>
  <details class="acc" open>
    <summary class="sum">{{ store.tUi('inspectorScene.title') }}</summary>
    <div class="body">
      <p class="hint">
        {{ store.tUi('inspectorScene.description') }}
      </p>
      <div class="dimRow" :aria-label="store.tUi('inspectorScene.previewDimsAria')">
        <div class="field">
          <label class="lbl lbl--dims" for="scene-w">{{ store.tUi('inspectorScene.width') }}</label>
          <input id="scene-w" v-model.number="sceneWidth" class="inp" type="number" min="1" step="1" />
        </div>
        <div class="field">
          <label class="lbl lbl--dims" for="scene-h">{{ store.tUi('inspectorScene.height') }}</label>
          <input id="scene-h" v-model.number="sceneHeight" class="inp" type="number" min="1" step="1" />
        </div>
      </div>

      <div class="group">
        <div class="lbl2 lbl2--section">{{ store.tUi('inspectorScene.resolutionPresets') }}</div>
        <p class="hint hint--tight">
          {{ store.tUi('inspectorScene.resolutionHint', { width: exportOutputWidth, height: exportOutputHeight }) }}
        </p>
        <ImockupPresetChips
          :options="RESOLUTION_PRESETS"
          :model-value="activeResolutionPreset"
          equal-width
          :group-label="store.tUi('inspectorScene.resolutionGroupAria')"
          :option-disabled="resolutionOptionDisabled"
          :option-title="resolutionOptionTitle"
          @select="onResolution"
        />
      </div>

      <div class="group">
        <div class="lbl2 lbl2--section">{{ store.tUi('inspectorScene.ratioPresets') }}</div>
        <p class="hint hint--tight">
          {{ store.tUi('inspectorScene.ratioHint') }}
        </p>
        <ImockupRatioGrid
          :model-value="activeRatioPreset"
          :group-label="store.tUi('inspectorScene.ratioGroupAria')"
          @select="onRatio"
        />
      </div>
    </div>
  </details>
</template>

<style scoped>
.acc {
  border: 0;
}

.sum {
  cursor: pointer;
  list-style: none;
  font-size: 0.95rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--im-text);
  padding: 0.15rem 0;
}

.sum::-webkit-details-marker {
  display: none;
}

.body {
  display: grid;
  gap: 0.75rem;
  padding-top: 0.65rem;
}

.dimRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
  align-items: start;
}

.field {
  display: grid;
  gap: 0.35rem;
  min-width: 0;
}

.lbl,
.lbl2 {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--im-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.lbl2 {
  margin-bottom: 0.15rem;
}

.lbl--dims {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 650;
  color: var(--im-text);
  font-size: 0.8125rem;
}

.lbl2--section {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
  color: var(--im-text);
  font-size: 0.8125rem;
}

.inp {
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  padding: 0.55rem 0.65rem;
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
  outline: none;
}

.inp:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 3px var(--im-focus-ring);
}

.group {
  display: grid;
  gap: 0.45rem;
}

.hint {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.45;
  color: var(--im-muted);
}

.hint--tight {
  margin-top: -0.2rem;
}

.mono {
  font-variant-numeric: tabular-nums;
  color: var(--im-text);
  font-weight: 650;
}
</style>
